import { inject, Injectable, signal } from "@angular/core";
import { HomophoneGroupClient } from "@clients/squidex/homophone-group.client";
import { EducationLevel, EducationLevelGroup } from "@domain/EducationLevel";
import { HomophoneGroup } from "@domain/HomophoneGroup";
import { AnalyticsService } from "@services/analytics.service";
import { ErrorBoundaryService } from "@services/error-boundary.service";
import Fuse from "fuse.js";

@Injectable({
  providedIn: "root",
})
export class HomophoneGroupService {
  private analyticsService = inject(AnalyticsService);
  private errorBoundaryService = inject(ErrorBoundaryService);
  private homophoneGroupClient = inject(HomophoneGroupClient);

  homophoneGroups = signal<HomophoneGroup[] | undefined>(undefined);
  homophoneGroupsForSelectedEducationLevels = signal<HomophoneGroup[] | undefined>(undefined);
  isLoading = signal(false);

  fuse = signal<Fuse<HomophoneGroup> | undefined>(undefined);
  fuseOptions = {
    includeScore: true,
    keys: ["suggestedAnswers", "pronunciation", "phonetics"],
    threshold: 0.2,
  };

  async fetchHomophoneGroups() {
    this.isLoading.set(true);
    this.homophoneGroups.set(undefined);
    try {
      const homophoneGroups = await this.homophoneGroupClient.getHomophoneGroups();

      if (!homophoneGroups || homophoneGroups.length === 0) {
        throw new Error("No homophone group found");
      }

      this.homophoneGroups.set(homophoneGroups);
    } catch (error) {
      if (error instanceof Error) {
        this.analyticsService.trackException(error);
      }
      this.errorBoundaryService.triggerError();
    }

    this.isLoading.set(false);
  }

  selectEducationLevelGroup(educationLevelGroup: EducationLevelGroup) {
    const filteredHomophoneGroups = this.filterHomophoneGroupBySelectedEducationLevels(educationLevelGroup.educationLevels);

    if (!filteredHomophoneGroups) {
      this.homophoneGroupsForSelectedEducationLevels.set(undefined);
      return;
    }

    this.homophoneGroupsForSelectedEducationLevels.set(filteredHomophoneGroups);
    this.fuse.set(new Fuse(filteredHomophoneGroups, this.fuseOptions));
  }

  private filterHomophoneGroupBySelectedEducationLevels(selectedEducationLevels: EducationLevel[]) {
    const homophoneGroups = this.homophoneGroups();

    if (!selectedEducationLevels || !homophoneGroups || homophoneGroups.length === 0) return undefined;

    return homophoneGroups.filter(homophoneGroup => {
      return homophoneGroup.educationLevels.some(educationLevel => {
        return selectedEducationLevels.includes(educationLevel);
      });
    });
  }

  searchHomophoneGroup(searchTerm = ""): HomophoneGroup[] {
    const homophoneGroup = this.homophoneGroupsForSelectedEducationLevels();
    const fuse = this.fuse();

    if (!homophoneGroup) return [];

    let results: HomophoneGroup[];

    if (!fuse) {
      return this.sortByDifficulty(homophoneGroup);
    } else if (searchTerm === "") {
      return this.sortByDifficulty(homophoneGroup);
    } else {
      return fuse.search(searchTerm).map(searchResult => searchResult.item);
    }

    return this.sortByDifficulty(results);
  }

  private sortByDifficulty(groups: HomophoneGroup[]): HomophoneGroup[] {
    return [...groups].sort((a, b) => a.difficulty - b.difficulty);
  }
}
